import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../../i18n/index.js';
import type { ReactElement } from 'react';

import { AlertTriangle, CheckCircle2, Minus } from 'lucide-react';

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { dealRiskRecommendationText, dealRiskSignalText } from '../../lib/dealRiskLabels.js';

interface DealCockpitSnippetProps {
  cockpit: Extract<StructuredMessagePayload, { type: 'deal_cockpit' }>['cockpit'];
  onProposeTouchStrategy?: ((dealId: string) => void) | undefined;
}

const STAGE_LABEL_KEYS: Record<string, string> = {
  New: 'crm.deals.stage.New',
  Contacted: 'crm.deals.stage.Contacted',
  Negotiation: 'crm.deals.stage.Negotiation',
  Won: 'crm.deals.stage.Won',
  Lost: 'crm.deals.stage.Lost',
};

const STAGE_CLASSES: Record<string, string> = {
  New: '!border-[var(--ant-color-info)] !bg-[var(--ant-color-info)] !text-white',
  Contacted: '!border-[var(--ant-color-warning)] !bg-[var(--ant-color-warning)] !text-white',
  Negotiation: '!border-[var(--ant-color-warning)] !bg-[var(--ant-color-warning)] !text-white',
  Won: '!border-[var(--ant-color-success)] !bg-[var(--ant-color-success)] !text-white',
  Lost: '!border-[var(--ant-color-error)] !bg-[var(--ant-color-error)] !text-white',
};

const HEALTH_CLASSES: Record<'low' | 'medium' | 'high', string> = {
  low: 'border-emerald-200 bg-emerald-50 text-emerald-950',
  medium: 'border-amber-200 bg-amber-50 text-amber-950',
  high: 'border-red-200 bg-red-50 text-red-950',
};

const HEALTH_BAR_CLASSES: Record<'low' | 'medium' | 'high', string> = {
  low: 'bg-emerald-500',
  medium: 'bg-amber-500',
  high: 'bg-red-500',
};

const QUALIFICATION_FIELD_LABEL_KEYS: Record<string, string> = {
  need: 'crm.chat.deal_cockpit.qualification_field.need',
  budget: 'crm.chat.deal_cockpit.qualification_field.budget',
  decisionMaker: 'crm.chat.deal_cockpit.qualification_field.decisionMaker',
  decisionDeadline: 'crm.chat.deal_cockpit.qualification_field.decisionDeadline',
  selectionCriteria: 'crm.chat.deal_cockpit.qualification_field.selectionCriteria',
};

/**
 * Tri-state box icon: confirmed facts get a check, known-missing facts a
 * warning, and "no data" a neutral dash — the icon is the whole status, the
 * checklist never renders textual status labels.
 */
function QualificationStatusIcon({
  status,
}: {
  status: 'unknown' | 'confirmed' | 'missing';
}): ReactElement {
  if (status === 'confirmed') {
    return (
      <CheckCircle2 size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-emerald-600" />
    );
  }
  if (status === 'missing') {
    return (
      <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600" />
    );
  }
  return <Minus size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-muted-foreground" />;
}

function formatDate(value: string | null | undefined, locale: string = 'ru'): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

function formatAmount(amount: number, currency: string, locale: string = 'ru'): string {
  return `${new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US').format(amount)} ${currency}`;
}

function priorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    low: i18n.t('crm.features.features.ai.chat.components.snippets.dealcockpitsnippet.nizkiy'),
    medium: i18n.t('crm.features.features.ai.chat.components.snippets.dealcockpitsnippet.sredniy'),
    high: i18n.t('crm.features.features.ai.chat.components.snippets.dealcockpitsnippet.vysokiy'),
    urgent: i18n.t('crm.features.features.ai.chat.components.snippets.dealcockpitsnippet.srochnyy'),
  };
  return labels[priority] ?? priority;
}

export function DealCockpitSnippet({
  cockpit,
  onProposeTouchStrategy,
}: DealCockpitSnippetProps): ReactElement {
  const { t, tc, locale } = useTranslation();
  const health = cockpit.health;
  const score = Math.min(100, Math.max(0, health.score));
  const stageLabel = t(STAGE_LABEL_KEYS[cockpit.deal.stage] ?? cockpit.deal.stage);
  const healthLabel = t(`crm.chat.deal_cockpit.health.${health.level}`);
  const nextActivity = formatDate(cockpit.deal.nextActivityDue, locale);
  const lastActivity = formatDate(cockpit.deal.lastActivityAt, locale);
  const recommendation = cockpit.recommendation
    ? dealRiskRecommendationText({ t, tc }, cockpit.recommendation)
    : null;

  return (
    <Card data-testid="structured-snippet-deal-cockpit" className="min-w-0 overflow-hidden">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-700">
              Deal Cockpit
            </p>
            <CardTitle className="break-words">{cockpit.deal.title}</CardTitle>
          </div>
          <Badge
            variant="info"
            className={`shrink-0 whitespace-nowrap ${STAGE_CLASSES[cockpit.deal.stage] ?? STAGE_CLASSES.New}`}
          >
            {stageLabel}
          </Badge>
        </div>
        <div className={`rounded-xl border p-3 ${HEALTH_CLASSES[health.level]}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium">{t('crm.chat.deal_cockpit.health_label')}</p>
              <p className="mt-1 text-lg font-semibold">{healthLabel}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold leading-none">{score}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide">/ 100</p>
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/70">
            <div
              className={`h-full rounded-full transition-[width] ${HEALTH_BAR_CLASSES[health.level]}`}
              style={{ width: `${score}%` }}
            />
          </div>
          <p className="mt-2 text-xs">
            {t('crm.chat.deal_cockpit.signals_found', { count: health.signalCount })}
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-sky-100 bg-sky-50 p-2.5 text-sky-950">
            <p className="text-[11px]">{t('crm.chat.deal_cockpit.amount')}</p>
            <p className="mt-1 font-semibold">
              {formatAmount(cockpit.deal.amount, cockpit.deal.currency, locale)}
            </p>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-amber-950">
            <p className="text-[11px]">{t('crm.chat.deal_cockpit.open_tasks')}</p>
            <p className="mt-1 font-semibold">{cockpit.metrics.openTaskCount}</p>
          </div>
          <div className="rounded-lg border border-orange-100 bg-orange-50 p-2.5 text-orange-950">
            <p className="text-[11px]">{t('crm.chat.deal_cockpit.overdue')}</p>
            <p className="mt-1 font-semibold">{cockpit.metrics.overdueTaskCount}</p>
          </div>
        </div>

        <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs text-muted-foreground">{t('crm.chat.deal_cockpit.contact')}</p>
          {cockpit.contact ? (
            <div className="mt-1 space-y-0.5">
              <p className="font-medium">{cockpit.contact.fullName}</p>
              {cockpit.contact.email ? <p>{cockpit.contact.email}</p> : null}
              {cockpit.contact.phone ? <p>{cockpit.contact.phone}</p> : null}
            </div>
          ) : (
            <p className="mt-1 text-muted-foreground">
              {t('crm.chat.deal_cockpit.contact_missing')}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">
              {t('crm.chat.deal_cockpit.next_activity')}
            </p>
            <p className="mt-1 font-medium">
              {nextActivity ?? t('crm.chat.deal_cockpit.not_scheduled')}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {t('crm.chat.deal_cockpit.last_activity')}
            </p>
            <p className="mt-1 font-medium">
              {lastActivity ?? t('crm.chat.deal_cockpit.no_activity')}
            </p>
          </div>
        </div>

        {cockpit.signals.length > 0 ? (
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              {t('crm.chat.deal_cockpit.risk_signals')}
            </p>
            <ul className="space-y-2">
              {cockpit.signals.slice(0, 3).map((signal, index) => (
                <li
                  key={`${signal.code}-${index}`}
                  className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-red-950"
                >
                  {dealRiskSignalText({ t, tc }, signal)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {cockpit.qualification && cockpit.qualification.items.length > 0 ? (
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              {t('crm.chat.deal_cockpit.qualification_title')}
            </p>
            <ul className="space-y-1.5">
              {cockpit.qualification.items.map((item) => (
                <li key={item.field} className="flex items-start gap-2">
                  <QualificationStatusIcon status={item.status} />
                  <span className="min-w-0 break-words">
                    <span className="font-medium">
                      {t(QUALIFICATION_FIELD_LABEL_KEYS[item.field] ?? item.field)}
                    </span>
                    {item.value ? (
                      <span className="text-muted-foreground"> — {item.value}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-950">
          <p className="text-xs font-medium text-amber-900">
            {t('crm.chat.deal_cockpit.next_best_action')}
          </p>
          {recommendation ? (
            <>
              <p className="mt-1 font-semibold">{recommendation.title}</p>
              <p className="mt-1 text-sm">{recommendation.description}</p>
            </>
          ) : null}
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            {t('crm.chat.deal_cockpit.tasks')}
          </p>
          {cockpit.tasks.length === 0 ? (
            <p className="text-muted-foreground">{t('crm.chat.deal_cockpit.tasks_empty')}</p>
          ) : (
            <ul className="space-y-2">
              {cockpit.tasks.slice(0, 3).map((task) => (
                <li
                  key={task.id}
                  className="rounded-lg border border-border/70 bg-muted/40 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium">{task.title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {priorityLabel(task.priority)}
                    </span>
                  </div>
                  {task.dueAt ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDate(task.dueAt, locale)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            {t('crm.chat.deal_cockpit.read_only')}
          </p>
          {onProposeTouchStrategy ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              data-testid="deal-cockpit-propose-touch-strategy"
              onClick={() => onProposeTouchStrategy(cockpit.deal.id)}
            >
              {t('crm.chat.touch_strategy.cockpit_button')}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
